/**
 * What she earns, and when.
 *
 * Three decisions shape this, all about being three:
 *
 *  1. **Unlocks arrive; they are not shopped for.** A shop is a choice, a choice is a decision, and a
 *     decision she cannot read is just a wall. Reaching the threshold hands her the thing.
 *  2. **The cadence is front-loaded.** Something new after the very first mission, then every second
 *     or third. Delayed gratification is not a three-year-old feature, and a reward she never reaches
 *     is indistinguishable from no reward at all.
 *  3. **Every prize is a thing she can see.** The table was twenty hats, and every one of them was
 *     invisible: the traced show pups arrive already wearing what identifies them - Marshall's helmet,
 *     Everest's bobble hat - so the rig hangs nothing, and a prize she cannot see is not a prize. Cars
 *     and stickers both show up somewhere she is already looking: on the chooser's grid, and on her own
 *     drawing.
 *
 * Pure data and pure functions: no clock, no storage, no randomness - and, because `src/core` may
 * depend on nothing (`scripts/check-core-purity.mjs`), no import of the content tables either. What is
 * granted is named here as plain strings and held in step with `src/content/` by `tests/unlocks.test.ts`.
 */

/**
 * What a prize *is*, which is what decides where its picture comes from and what owning it changes.
 *
 * `'hat'` is gone with the hats. Widening this union was the first move of the rework and the compiler
 * found every place that had assumed one kind, which is exactly what it was kept as a field for.
 */
export type UnlockKind = 'vehicle' | 'sticker-set';

export interface UnlockDef {
  readonly id: string;
  readonly kind: UnlockKind;
  /** Paw points needed. Ordered so cheaper things arrive first. */
  readonly cost: number;
  /** Parent-facing only. */
  readonly label: string;
  /**
   * What owning this hands her: one vehicle id, or the stickers in the set.
   *
   * Ids from the content tables (`src/content/vehicles.ts`, `src/content/stickers.ts`), which core cannot
   * import - so a name here that nothing in the pack answers to is a prize that reveals a blank card, and
   * the test that catches it is the same one that used to check a hat had art on disk.
   */
  readonly grants: readonly string[];
}

/**
 * Costs assume 10 points a mission, so the thresholds read as mission counts: 1, 2, 4, 6, 10, 16, 24. The
 * first arrives immediately, which is the one that teaches her that finishing earns something.
 *
 * **A car is the first prize**, which answers the question the design doc left open about whether the
 * ladder should announce itself early: nothing announces it better than the picker having one more car in
 * it than it did an hour ago.
 *
 * The ladder is seven rows where the hats were twenty, and that is a real cost rather than an oversight -
 * it is the trade the rework accepted, because five cars and two sticker sets are seven *visible* prizes
 * against twenty invisible ones. What refills the tail is the design doc's own list: trailer paint, new
 * things to fetch, a seventh place, horn sounds, a second pup in the cab.
 *
 * Sticker sets are three at a time, because three reads as a present and one reads as a trickle. Everything
 * in the tray that is not named in a set is hers from the start, so a new sticker is free until a row here
 * says otherwise.
 */
export const UNLOCKS: readonly UnlockDef[] = [
  { id: 'car-rubble', kind: 'vehicle', cost: 10, label: "Rubble's digger", grants: ['rubble-digger'] },
  {
    id: 'stickers-bright',
    kind: 'sticker-set',
    cost: 20,
    label: 'flower, bolt and smiley stickers',
    grants: ['flower', 'bolt', 'smile'],
  },
  {
    id: 'car-rocky',
    kind: 'vehicle',
    cost: 40,
    label: "Rocky's recycling truck",
    grants: ['rocky-recycler'],
  },
  {
    id: 'car-zuma',
    kind: 'vehicle',
    cost: 60,
    label: "Zuma's hovercraft",
    grants: ['zuma-hovercraft'],
  },
  {
    id: 'car-everest',
    kind: 'vehicle',
    cost: 100,
    label: "Everest's snowcat",
    grants: ['everest-snowcat'],
  },
  { id: 'car-tracker', kind: 'vehicle', cost: 160, label: "Tracker's jeep", grants: ['tracker-jeep'] },
  {
    id: 'stickers-balloons',
    kind: 'sticker-set',
    cost: 240,
    label: 'the balloons',
    grants: ['balloon', 'heart-balloon', 'star-balloon'],
  },
];

export function unlockById(id: string): UnlockDef | null {
  return UNLOCKS.find((unlock) => unlock.id === id) ?? null;
}

/**
 * Everything now affordable that she does not already have, cheapest first.
 *
 * Returns a list rather than a single item because points can jump - a bonus, or a save restored on a
 * new device - and skipping an unlock she has paid for would be quietly unfair.
 */
export function pendingUnlocks(points: number, owned: readonly string[]): readonly UnlockDef[] {
  const have = new Set(owned);
  return UNLOCKS.filter((unlock) => !have.has(unlock.id) && unlock.cost <= points).sort(
    (a, b) => a.cost - b.cost,
  );
}

/** The next thing to look forward to, or null once she has everything. */
export function nextUnlock(points: number, owned: readonly string[]): UnlockDef | null {
  const have = new Set(owned);
  const remaining = UNLOCKS.filter((unlock) => !have.has(unlock.id)).sort(
    (a, b) => a.cost - b.cost,
  );
  return remaining.find((unlock) => unlock.cost > points) ?? remaining[0] ?? null;
}

/** How close she is to the next thing, 0 to 1. Drives the treat jar's fill level. */
export function progressToNext(points: number, owned: readonly string[]): number {
  const next = nextUnlock(points, owned);
  if (next === null) return 1;

  const have = new Set(owned);
  const previousCost = UNLOCKS.filter((unlock) => have.has(unlock.id) || unlock.cost <= points)
    .map((unlock) => unlock.cost)
    .reduce((highest, cost) => Math.max(highest, cost), 0);

  const span = next.cost - previousCost;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (points - previousCost) / span));
}

export interface ClaimResult {
  readonly owned: readonly string[];
  readonly revealed: readonly UnlockDef[];
}

/**
 * Hand over everything she has earned. Idempotent: claiming twice reveals nothing the second time, so
 * a screen that remounts cannot double-announce a prize.
 */
export function claimUnlocks(points: number, owned: readonly string[]): ClaimResult {
  const revealed = pendingUnlocks(points, owned);
  if (revealed.length === 0) return { owned, revealed };
  return { owned: [...owned, ...revealed.map((unlock) => unlock.id)], revealed };
}

/**
 * Everything of one kind that the ladder hands out - the cars and the stickers that are *prizes*.
 *
 * This is how a content table tells a prize from a freebie without carrying a second copy of the answer.
 * A flag on each entry was the obvious alternative and it was worse twice over: two tables to keep in
 * step, and a sticker added to the tray with the flag forgotten would have been silently locked away
 * behind a prize nothing grants.
 */
export function prizeIds(kind: UnlockKind): readonly string[] {
  return UNLOCKS.filter((unlock) => unlock.kind === kind).flatMap((unlock) => unlock.grants);
}

/** The ones of that kind she has actually earned. Unknown ids in the save are ignored. */
export function ownedPrizeIds(kind: UnlockKind, owned: readonly string[]): readonly string[] {
  const have = new Set(owned);
  return UNLOCKS.filter((unlock) => unlock.kind === kind && have.has(unlock.id)).flatMap(
    (unlock) => unlock.grants,
  );
}
